import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { ClientRepositoryBase, deserializeDocumentClientModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentPaymentListItemModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, ExchangeRateModel, PurchaseBankAccountModel, PurchaseExpenseDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO, PurchaseVatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepository } from '../client/client.repo';
import { PurchaseExpenseDocumentBO } from './purchase-expense-document.bo';

@Repository({
    boType: PurchaseExpenseDocumentBO,
    apiController: TaggedUrl.join('purchase', [Tags.Api]),
    dto: {
        get: PurchaseGetDocumentDTO,
        update: PurchaseUpdateDocumentDTO
    }
})
@Injectable({ providedIn: 'root'})
export class PurchaseExpenseDocumentRepository extends RepositoryBase<PurchaseExpenseDocumentBO, PurchaseExpenseDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO> {
    public constructor(
        http: Http,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly clientRepository: ClientRepository
    ) {
        super(http as HttpService);
    }

    protected createBO(): PurchaseExpenseDocumentBO {
        return new PurchaseExpenseDocumentBO(this.weaver as WeaverBase, this.queryBus as any as QueryBusBase, this.clientRepository as ClientRepositoryBase);
    }

    protected async fromPlainObjectAsync(item: PurchaseGetDocumentDTO): Promise<PurchaseExpenseDocumentModel> {
        const vatTable: ObservableCollection<PurchaseVatTableItemModel> = new ObservableCollection<PurchaseVatTableItemModel>();
        item.vatTable.items.forEach(element => {
            vatTable.push(new PurchaseVatTableItemModel(pickBy({
                currencyGrossValue: element.currencyGross,
                currencyNetValue: element.currencyNet,
                currencyVatValue: element.currencyVat,
                grossValue: element.gross,
                manual: element.manual,
                netValue: element.net,
                vatRateId: element.vatRateId,
                vatValue: element.vat,
            }, val => val !== void 0)));
        });

        const converted = new PurchaseExpenseDocumentModel(pickBy<Partial<PurchaseExpenseDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,

            ddkState: item.ddkState,
            eppState: item.eppState,
            jpk_VATState: item.jpk_vatState,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            seller: deserializeDocumentClientModel(item.seller),
            bankAccount: item.sellerBankAccount
                ? new PurchaseBankAccountModel(pickBy<PurchaseBankAccountModel>({
                    bankName: item.sellerBankAccount.bankName,
                    number: item.sellerBankAccount.number,
                    status: item.sellerBankAccount.whiteListStatus,
                    statusDate: item.sellerBankAccount.whiteListLastDateCheck,
                    statusId: item.sellerBankAccount.whiteListCheckingId
                }, val => val !== void 0))
                : null,
            documentNumber: item.documentNumber,
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                units: item.exchangeRate.units,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
            }, val => val !== void 0)) : null,
            expenseName: item.expenseName,
            externalDocumentNumber: item.externalDocumentNumber,
            kind: item.kind,
            note: item.note,
            numberingId: item.numberingId,
            splitPayment: item.splitPayment,
            jpkVatGroups: item.jpkVatDocumentGroups,

            ksefSubmissionMode: item.ksefSubmissionMode,
            ksefHash: item.ksefHash,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefHeaderId: item.ksefHeaderId,
            ksefDocumentId: item.ksefDocumentId,
            dateOfAssigningKsefNumber: item.dateOfAssigningKsefNumber,

            grossValue: item.totalBaseCurrencyGrossValue,

            //boolean
            fixedAsset: item.fixedAsset,
            favourite: item.favourite,
            issuedByVatPayer: item.issuedByVatPayer,

            //date
            deliveryDate: item.deliveryDate,
            dueDate: item.dueDate ? new Date(item.dueDate) : null,
            issueDate: item.issueDate ? new Date(item.issueDate) : null,
            payments: new ObservableCollection(item.payments.map(item => {
                return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                    id: item.id,
                    date: item.date,
                    value: item.amount,
                    onDocument: item.onDocument
                }, val => val !== void 0));
            })),
            receiptDate: item.receiptDate ? new Date(item.receiptDate) : null,
            vatDeductionDate: item.vatDeductionDate,

            //ids
            communityTransactionId: item.communityTransactionId,

            vatTable: vatTable,
            currencyId: item.currencyId,
            paymentMethodId: item.paymentMethodId,
        }, val => val !== void 0));

        return converted;
    }

    protected async preparePlainObjectAsync(model: PurchaseExpenseDocumentModel): Promise<PurchaseUpdateDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,

            ksefDocumentId: model.ksefDocumentId,
            ksefSubmissionMode: model.ksefSubmissionMode,
            originalEInvoiceDocumentId: model.originalEInvoiceDocumentId,

            calculationMethod: model.calculationMethod,
            cashBasis: model.cashBasis,
            seller: serializeDocumentClientModel(model.seller),
            sellerBankAccount: model.bankAccount
                ? {
                    bankName: model.bankAccount.bankName,
                    number: model.bankAccount.number,
                    whiteListStatus: model.bankAccount.status,
                    whiteListLastDateCheck: model.bankAccount.statusDate,
                    whiteListCheckingId: model.bankAccount.statusId
                }
                : null,
            documentNumber: model.documentNumber,
            expenseName: model.expenseName,
            externalDocumentNumber: model.externalDocumentNumber,
            kind: model.kind,
            note: model.note,
            numberingId: model.numberingId,
            splitPayment: model.splitPayment,
            jpkVatDocumentGroups: model.jpkVatGroups,

            //boolean
            fixedAsset: model.fixedAsset,
            favourite: model.favourite,

            //date
            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            vatDeductionDate: getStartOfDate(model.vatDeductionDate),
            receiptDate: model.receiptDate ? getStartOfDate(model.receiptDate) : null,
            deliveryDate: getStartOfDate(model.deliveryDate),
            dueDate: model.dueDate ? getStartOfDate(model.dueDate) : null,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),

            //ids
            communityTransactionId: model.communityTransactionId,
            vatTable: {
                items: model.vatTable.map(item => {
                    return {
                        currencyGross: item.currencyGrossValue,
                        currencyNet: item.currencyNetValue,
                        currencyVat: item.currencyVatValue,
                        gross: item.grossValue,
                        manual: item.manual,
                        net: item.netValue,
                        vatRateId: item.vatRateId,
                        vat: item.vatValue,
                    };
                })
            },
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            currencyId: model.currencyId,
            paymentMethodId: model.paymentMethodId,
            inventoryEffect: null,
            items: null,
            transactionLocation: null,
            supplier: null,
            issuer: null,
            recipient: null,
            relatedInventoryDocuments: null
        };
    }
}
